import {Box, Grid} from '@mui/material';
import {useItemDataHelper, useItemStatuesInRedux, useUpdateItemDataAPI} from 'hooks';
import {BoardItemDrawerProps} from './BoardItemDrawer';

import {ReminderModel} from 'models';
import {ItemDataUpdateRequest, itemDataUpdateRequestDefault} from 'models/board/itemData';
import {SprintStatus} from 'models/sprint.model';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {sprintSelector} from 'store/sprints';
import {AlertItemClose, BoardItemSectionRenderer, ItemDrawerHeader} from './component';
import {BoardItemDialogSkeleton} from './Skeleton';

export const BoardItemDialog: React.FC<BoardItemDrawerProps> = ({
    handleDrawerClose,
    removeItemDataFromState,
    handleUpdateItemDataState,
    getItemDataByIdLoading,
    getItemDataByIdResponse,
    itemStatuses,
    boardId,
}) => {
    const {updateItemData, updateItemDataLoading, updateItemDataResponse} = useUpdateItemDataAPI();
    const {selectedSprint} = useSelector(sprintSelector);
    const {checkTheDifferent} = useItemDataHelper();
    const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
    const [saveAndCloseTheDrawer, setSaveAndCloseTheDrawer] = useState<boolean>(false);

    const {handleUpdateItemStatusCountAfterUpdate} = useItemStatuesInRedux();

    // Memoize form instance to avoid unnecessary re-renders
    const form = useForm<ItemDataUpdateRequest>({
        defaultValues: itemDataUpdateRequestDefault,
    });
    const {setValue, control, handleSubmit, getValues, watch} = form;
    const [openAddUpdateReminderDialog, setOpenAddUpdateReminderDialog] = useState(false);
    const [itemDataByIdDefaultValues, setItemDataByIdDefaultValues] = useState<ItemDataUpdateRequest | undefined>();

    // Handle updates after submitting data

    useEffect(() => {
        if (!updateItemDataLoading && updateItemDataResponse) {
            handleUpdateItemStatusCountAfterUpdate({
                prevStatusId: getItemDataByIdResponse?.itemStatusId || '',
                newStatusId: updateItemDataResponse?.itemStatusId || '',
                draggedItemId: updateItemDataResponse?.id,
            });
            const perviousValues: ItemDataUpdateRequest = {
                id: updateItemDataResponse?.id,
                itemStatusId: updateItemDataResponse?.itemStatusId,
                data: updateItemDataResponse.data,
                order: updateItemDataResponse?.order,
                sprintId: updateItemDataResponse?.sprintId,
                url: updateItemDataResponse?.url,
                ticketNo: updateItemDataResponse?.ticketNo,
            };
            setItemDataByIdDefaultValues(perviousValues);

            if (
                updateItemDataResponse &&
                selectedSprint !== null &&
                selectedSprint.sprintStatus !== SprintStatus.All &&
                updateItemDataResponse.sprintId !== selectedSprint.id
            ) {
                removeItemDataFromState(updateItemDataResponse.id);
            } else {
                handleUpdateItemDataState(updateItemDataResponse);
            }
            if (saveAndCloseTheDrawer) {
                handleDrawerClose();
                setSaveAndCloseTheDrawer(false);
            }
        }
    }, [updateItemDataResponse, updateItemDataLoading]);

    // Optimize onSubmitHandler to avoid unnecessary Promise.all if not needed
    const onSubmitHandler = (data: ItemDataUpdateRequest) => {
        data.sprintId = data.sprintId === '' ? null : data.sprintId;
        const {ticketNo, ...restData} = data;
        updateItemData(restData);
        // Promise.all(
        //     data.data.map(async (field: ItemDataFieldModel) => {
        //         if (
        //             field.fieldType === FieldTypeEnum.UploadFile &&
        //             Array.isArray(field.files) &&
        //             field.files.length > 0
        //         ) {
        //             const uploadToBeFiles = field.files.filter((x: any) => x.preview && x.preview.includes('blob:'));
        //             if (uploadToBeFiles.length > 0) {
        //                 const response = await storeMediaFiles({
        //                     variables: {
        //                         files: uploadToBeFiles,
        //                         objectId: data.id,
        //                         objectType: MediaFileObjectTypeEnum.ITEM_DATA,
        //                     },
        //                 });
        //                 if (response && response.data && response.data.storeMediaFiles) {
        //                     const previousFilesValues = field.value.value?.includes('[')
        //                         ? JSON.parse(field.value.value)
        //                         : [];
        //                     const newFilesValues = response.data.storeMediaFiles.map((x: any) => x.partialFilePath);
        //                     field.value.value = JSON.stringify([...previousFilesValues, ...newFilesValues]);
        //                 }
        //             }
        //         }
        //         // If files is not an array or is empty, skip upload logic
        //     }),
        // ).then(() => {
        //     data.sprintId = data.sprintId === '' ? null : data.sprintId;
        //     data.data.forEach(field => delete field.files);
        //     const {ticketNo, ...restData} = data;

        // });
    };

    // Only reset form when the item ID changes
    useEffect(() => {
        if (getItemDataByIdResponse && !getItemDataByIdLoading) {
            setValue('id', getItemDataByIdResponse.id);
            setValue('itemStatusId', getItemDataByIdResponse.itemStatusId);
            setValue('data', getItemDataByIdResponse.data);
            setValue('order', getItemDataByIdResponse.order);
            setValue('sprintId', getItemDataByIdResponse.sprintId ?? '');
            setValue('url', getItemDataByIdResponse.url);
            setValue('ticketNo', getItemDataByIdResponse.ticketNo);
            setItemDataByIdDefaultValues({
                id: getItemDataByIdResponse.id,
                itemStatusId: getItemDataByIdResponse.itemStatusId,
                data: getItemDataByIdResponse.data,
                order: getItemDataByIdResponse.order,
                sprintId: getItemDataByIdResponse.sprintId,
                url: getItemDataByIdResponse.url,
                ticketNo: getItemDataByIdResponse.ticketNo,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getItemDataByIdResponse?.id]);

    // Memoize formHasChanges to avoid unnecessary recalculations
    const formHasChanges = React.useCallback((): boolean => {
        if (itemDataByIdDefaultValues) {
            return checkTheDifferent(getValues(), itemDataByIdDefaultValues);
        }
        return false;
    }, [itemDataByIdDefaultValues, checkTheDifferent, getValues]);

    // Optimize handleSaveOnCloseConfirm to avoid creating a new function on every render
    const handleSaveOnCloseConfirm = React.useCallback(() => {
        setSaveAndCloseTheDrawer(true);
        setIsConfirmPopupOpen(false);
        handleSubmit(onSubmitHandler)();
    }, [handleSubmit, onSubmitHandler]);

    // Optimize handleSaveOnCloseDiscard to avoid creating a new function on every render
    const handleSaveOnCloseDiscard = React.useCallback(() => {
        setIsConfirmPopupOpen(false);
        handleDrawerClose();
    }, [handleDrawerClose]);

    // Optimize handleDialogClose to avoid creating a new function on every render
    const handleDialogClose = React.useCallback(() => {
        formHasChanges() ? setIsConfirmPopupOpen(true) : handleDrawerClose();
    }, [formHasChanges, handleDrawerClose]);

    return (
        <>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                {getItemDataByIdLoading && !getItemDataByIdResponse ? (
                    <BoardItemDialogSkeleton />
                ) : (
                    <Box p={1} pl={2}>
                        {getItemDataByIdResponse && (
                            <ItemDrawerHeader
                                boardSlug={boardId}
                                handleDrawerClose={handleDialogClose}
                                item={getItemDataByIdResponse}
                                control={control}
                                ticketNo={getItemDataByIdResponse.ticketNo}
                                loading={getItemDataByIdLoading || updateItemDataLoading}
                            />
                        )}
                        <Grid container py={1}>
                            <Grid item xs={12}>
                                {getItemDataByIdResponse && (
                                    <BoardItemSectionRenderer
                                        boardSlug={boardId}
                                        loading={getItemDataByIdLoading || updateItemDataLoading}
                                        setOpenAddUpdateReminderDialog={setOpenAddUpdateReminderDialog}
                                        openAddUpdateReminderDialog={openAddUpdateReminderDialog}
                                        onRemoveItemResponseHandler={(id, drawerClose) => {
                                            removeItemDataFromState(id);
                                            drawerClose && handleDrawerClose();
                                        }}
                                        control={control}
                                        setValue={setValue}
                                        item={getItemDataByIdResponse}
                                        watch={watch}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </form>
            <AlertItemClose
                onDiscardClickHandler={handleSaveOnCloseDiscard}
                onConfirmClickHanlder={handleSaveOnCloseConfirm}
                onCancelClickHandler={() => setIsConfirmPopupOpen(false)}
                open={isConfirmPopupOpen}
            />
        </>
    );
};

export default BoardItemDialog;
